import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface PhilosophyPillar {
  icon: string;
  title: string;
  description: string;
  principles: string[];
}

const philosophyPillars: PhilosophyPillar[] = [
  {
    icon: 'ChartBarIcon',
    title: 'Structured Progression',
    description: 'Every journey begins with a single step, but success comes from knowing which step to take next.',
    principles: [
      'Individualized assessment and baseline establishment',
      'Progressive overload with measurable milestones',
      'Regular evaluation and program adjustment',
      'Long-term development over quick fixes'
    ]
  },
  {
    icon: 'HeartIcon',
    title: 'Character Through Training',
    description: 'Physical transformation is just the beginning. True growth happens in the mind and spirit.',
    principles: [
      'Discipline built through consistent practice',
      'Resilience developed through challenging workouts',
      'Confidence earned through measurable progress',
      'Community support fostering accountability'
    ]
  },
  {
    icon: 'AcademicCapIcon',
    title: 'Education-First Approach',
    description: 'Understanding the "why" behind training creates independent, informed athletes for life.',
    principles: [
      'Exercise science fundamentals for all members',
      'Injury prevention and body awareness training',
      'Nutrition guidance integrated with fitness goals',
      'Empowerment through knowledge and understanding'
    ]
  },
  {
    icon: 'UserGroupIcon',
    title: 'Inclusive Excellence',
    description: 'Elite methodologies accessible to everyone, regardless of starting point or age.',
    principles: [
      'Age-appropriate program modifications',
      'Scalable intensity for all fitness levels',
      'Supportive environment without intimidation',
      'Celebration of individual progress and achievements'
    ]
  }
];

const PhilosophySection = () => {
  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-theme-orange/10 rounded-full mb-6">
            <span className="text-theme-orange font-heading font-semibold text-sm">Our Philosophy</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-primary mb-6 leading-tight">
            Training Methodology &<br />Core Principles
          </h2>
          <p className="text-lg text-text-secondary font-body max-w-3xl mx-auto leading-relaxed">
            GO Speed's approach combines championship-level training science with inclusive, supportive coaching. We believe fitness is a lifelong practice, not a temporary pursuit.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {philosophyPillars.map((pillar, index) => (
            <div
              key={index}
              className="bg-card rounded-2xl p-8 shadow-card hover:shadow-hover transition-all duration-300 border border-border"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-theme-orange/10 rounded-xl mb-6">
                <Icon name={pillar.icon as any} size={32} className="text-theme-orange" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-primary mb-4">
                {pillar.title}
              </h3>
              <p className="text-text-secondary font-body mb-6 leading-relaxed">
                {pillar.description}
              </p>
              <ul className="space-y-3">
                {pillar.principles.map((principle, idx) => (
                  <li key={idx} className="flex items-start">
                    <Icon name="CheckCircleIcon" size={20} className="text-success mr-3 mt-0.5 flex-shrink-0" variant="solid" />
                    <span className="text-text-secondary text-sm font-body leading-relaxed">
                      {principle}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-theme-black rounded-2xl p-8 lg:p-12 text-center shadow-modal">
          <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            The GO Speed Difference
          </h3>
          <p className="text-white/90 font-body text-lg max-w-3xl mx-auto leading-relaxed mb-8">
            We don't sell quick fixes or trendy workouts. We build character through structured progression, making fitness a lifelong practice. Our members don't just get fit – they develop the discipline, confidence, and knowledge to maintain wellness for life.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-white/10 rounded-lg px-6 py-3">
              <span className="text-white font-heading font-semibold">No Gimmicks</span>
            </div>
            <div className="bg-white/10 rounded-lg px-6 py-3">
              <span className="text-white font-heading font-semibold">Science-Based</span>
            </div>
            <div className="bg-white/10 rounded-lg px-6 py-3">
              <span className="text-white font-heading font-semibold">Results-Driven</span>
            </div>
            <div className="bg-white/10 rounded-lg px-6 py-3">
              <span className="text-white font-heading font-semibold">Community-Focused</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;